import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@hub-la/shadcn'
import { VerificationStatus, useGetIdentity } from '@hub-la/fe-get-user'
import { useRoleplay } from '@hub-la/fe-roleplay'
import React, { ComponentType, ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountVerificationModal } from '../pages/account-verification-modal'
import { AccountVerificationProvider } from '../providers/account-verification-provider'

type ConfirmationModalProps = {
  open: boolean
  onSubmit: () => void
  onCancel: () => void
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = (props) => {
  const { open, onSubmit, onCancel } = props
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('confirmationModal.create.title')}</DialogTitle>
          <DialogDescription>{t('confirmationModal.create.message')}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-end">
          <Button variant="outline" onClick={onCancel}>
            {t('confirmationModal.create.cancel')}
          </Button>
          <Button onClick={onSubmit}>{t('confirmationModal.create.confirm')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type RoleplayModalProps = {
  open: boolean
  onCancel: () => void
}

const RoleplayModal: React.FC<RoleplayModalProps> = (props) => {
  const { open, onCancel } = props
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('roleplayModal.create.title')}</DialogTitle>
          <DialogDescription>{t('roleplayModal.create.message')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('roleplayModal.create.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type WithAccountVerificationProps = {
  onClick?: () => void
}

type Options = {
  tooltip?: string
}

export const withAccountVerification = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: Options,
): React.FC<P & WithAccountVerificationProps> => {
  return (props: P & WithAccountVerificationProps): ReactElement => {
    const [accountVerificationOpen, setAccountVerificationOpen] = useState(false)
    const [confirmationOpen, setConfirmationOpen] = useState(false)
    const [roleplayOpen, setRoleplayOpen] = useState(false)

    const { isRoleplay } = useRoleplay()
    const { data } = useGetIdentity()

    const hasApprovedAccount = data?.identity?.verification?.status === VerificationStatus.APPROVED

    const openConfirmationModal = () => {
      if (isRoleplay && !hasApprovedAccount) {
        setRoleplayOpen(true)
        return
      }

      if (hasApprovedAccount) {
        props.onClick?.()
        return
      }
      setConfirmationOpen(true)
    }

    const closeConfirmationModal = () => {
      setConfirmationOpen(false)
    }

    const closeRoleplayModal = () => {
      setRoleplayOpen(false)
    }

    const openAccountVerificationModal = () => {
      setConfirmationOpen(false)
      setAccountVerificationOpen(true)
    }

    const closeAccountVerificationModal = () => {
      setAccountVerificationOpen(false)
      setConfirmationOpen(false)
    }

    const onFinish = () => {
      closeAccountVerificationModal()
    }

    return (
      <AccountVerificationProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <WrappedComponent {...props} onClick={openConfirmationModal} disabled={!hasApprovedAccount} />
              </div>
            </TooltipTrigger>
            {options?.tooltip && !hasApprovedAccount && <TooltipContent>{options.tooltip}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
        <ConfirmationModal
          open={confirmationOpen}
          onSubmit={openAccountVerificationModal}
          onCancel={closeConfirmationModal}
        />
        <RoleplayModal open={roleplayOpen} onCancel={closeRoleplayModal} />
        <AccountVerificationModal
          open={accountVerificationOpen}
          onClose={closeAccountVerificationModal}
          onFinish={onFinish}
        />
      </AccountVerificationProvider>
    )
  }
}
